// Subpath import, not the package root: the root barrel (@financialharmony/api) re-exports the
// buf-generated proto services too, which can pull their descriptors into whatever bundles this
// module — this package only needs the plain header-name constant. See
// packages/api/package.json's "./correlation" export.
import { ERROR_CODE_HEADER, ERROR_PARAMS_HEADER, REFERENCE_HEADER } from "@financialharmony/api/correlation"

/**
 * Reading a *structured* failure code off an error, rather than pattern-matching its message.
 *
 * `apps/client`'s edge-function proxy attaches `ServiceError.errorType` (e.g. `KYC_REQUIRED`) to
 * every error it throws, precisely so callers can branch on a stable code. Components in this
 * package cannot import from `apps/client`, so the contract is restated here as a property
 * check — deliberately narrow, and the only thing this package assumes about the transport.
 *
 * Why this exists at all (FIN-381): the KYC signal used to travel as free text. A server action
 * substring-matched the backend's message for "KYC", returned a sentinel string, the trigger
 * re-fabricated the backend's sentence from memory, and this package substring-matched *that*.
 * Two free-text matches and a hand-copied backend string, all of which break silently if anyone
 * rewords `KycRequiredError` — and all of which fire wrongly on any unrelated message that
 * happens to contain "KYC".
 */
export function errorCodeOf(error: unknown): string | undefined {
  if (error && typeof error === "object" && "errorCode" in error) {
    const code = (error as { errorCode?: unknown }).errorCode
    if (typeof code === "string") return code
  }
  // A browser-side Connect call: the BFF's property did not survive the hop, its metadata did (FIN-1524).
  const fromMetadata = metadataOf(error)?.get?.(ERROR_CODE_HEADER)
  return fromMetadata && fromMetadata.trim() !== "" ? fromMetadata : undefined
}

/** The values a refusal's catalog sentence is filled with (`ServiceError.params` on the server). */
export type ErrorParams = Record<string, string | number>

const MAX_ERROR_PARAMS = 8
const MAX_ERROR_PARAM_LENGTH = 200

/**
 * Keeps only short plain strings and finite numbers: the params fill a catalog sentence, so anything
 * else (nested data, a long blob) is dropped rather than put on a screen. Undefined when none remain.
 */
export function sanitizeErrorParams(value: unknown): ErrorParams | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  const kept: ErrorParams = {}
  for (const [key, entry] of Object.entries(value as Record<string, unknown>).slice(0, MAX_ERROR_PARAMS)) {
    const usable = typeof entry === "string" ? entry.length <= MAX_ERROR_PARAM_LENGTH : typeof entry === "number" && Number.isFinite(entry)
    if (usable) kept[key] = entry as string | number
  }
  return Object.keys(kept).length > 0 ? kept : undefined
}

/** The parameters of a coded refusal's sentence: a property (same-process caller) or the Connect metadata header. */
export function errorParamsOf(error: unknown): ErrorParams | undefined {
  if (error && typeof error === "object" && "errorParams" in error) {
    const params = sanitizeErrorParams((error as { errorParams?: unknown }).errorParams)
    if (params) return params
  }
  const header = metadataOf(error)?.get?.(ERROR_PARAMS_HEADER)
  if (!header) return undefined
  try {
    return sanitizeErrorParams(JSON.parse(decodeURIComponent(header)))
  } catch {
    // tier3: a header that is not JSON carries no params; the sentence then falls back to the caller's own text
    return undefined
  }
}

function metadataOf(error: unknown): Headers | undefined {
  return (error as { metadata?: Headers } | null | undefined)?.metadata
}

/**
 * FIN-1406: the closed cross-domain axis alongside `errorCodeOf`'s specific `errorType` — see
 * `ErrorCategory`'s doc comment (`supabase/functions/_shared/utils/errors.ts`). Read the same
 * duck-typed way as `errorCodeOf`, for the same reason: this package cannot import the transport.
 */
export function errorCategoryOf(error: unknown): string | undefined {
  if (error && typeof error === "object" && "errorCategory" in error) {
    const category = (error as { errorCategory?: unknown }).errorCategory
    return typeof category === "string" ? category : undefined
  }
  return undefined
}

/** `ServiceError.errorCategory` meaning the bank/tax/ERP provider's own backend is failing or
 * throttling right now — never the user's fault, safe to auto-retry. */
export const PROVIDER_UNAVAILABLE_CATEGORY = "upstream_unavailable"

/** Whether this error's `errorCategory` is the provider-unavailable bucket above. */
export function isProviderUnavailableError(error: unknown): boolean {
  return errorCategoryOf(error) === PROVIDER_UNAVAILABLE_CATEGORY
}

/** `ServiceError.errorType` for a company that has not completed the provider's KYC. */
export const KYC_REQUIRED_CODE = "KYC_REQUIRED"

/** Whether this error is the provider's "this company must complete KYC first" refusal. */
export function isKycRequiredError(error: unknown): boolean {
  return errorCodeOf(error) === KYC_REQUIRED_CODE
}

/**
 * `ServiceError.errorType` for a tax authority inside one of its published scheduled closures
 * (FIN-1003). Its message says when the authority reopens, which is the one failure sentence the
 * tax-account flows show verbatim.
 */
export const TAX_AUTHORITY_CLOSED_CODE = "TAX_AUTHORITY_CLOSED"

/** Whether this error is a tax authority's scheduled closure — not a closed tax *account*. */
export function isTaxAuthorityClosedError(error: unknown): boolean {
  return errorCodeOf(error) === TAX_AUTHORITY_CLOSED_CODE
}

/**
 * `ServiceError.errorType`s for a tax-account connect the server refuses for the company itself:
 * a legal form whose connect is not supported yet (FIN-1130), a sole trader's stored number that
 * cannot be resolved (FIN-1130, FIN-1552), a legal form with no tax profile (FIN-1167), and a
 * taxpayer another company already holds a live tax account for (FIN-1167 EC5).
 * Retrying cannot help, so the message is shown verbatim instead of a "try again".
 */
const TAX_CONNECT_NOT_ALLOWED_CODES = [
  "TAX_SOLE_TRADER_UNSUPPORTED",
  "TAX_SOLE_TRADER_NUMBER_UNVERIFIED",
  "TAX_SOLE_TRADER_SAMORDNINGSNUMMER",
  "TAX_LEGAL_FORM_UNKNOWN",
  "TAX_PRINCIPAL_ALREADY_CONNECTED",
] as const

export type TaxConnectNotAllowedCode = (typeof TAX_CONNECT_NOT_ALLOWED_CODES)[number]

/** The connect refusal's code when this error is one, else null. */
export function taxConnectNotAllowedCodeOf(error: unknown): TaxConnectNotAllowedCode | null {
  const code = errorCodeOf(error)
  return (TAX_CONNECT_NOT_ALLOWED_CODES as readonly (string | undefined)[]).includes(code)
    ? (code as TaxConnectNotAllowedCode)
    : null
}

/**
 * The text to put in front of a person when something failed.
 *
 * Three things reach a `catch` in this app and only one of them is meant to be read:
 *
 * - A `ConnectError` — everything that crosses the BFF arrives as one. Its `.message` is
 *   `"[invalid_argument] <the real sentence>"`, because Connect prepends the wire code.
 *   The sentence the backend wrote for a human is `.rawMessage`, and that is what a person
 *   should see; `[invalid_argument]` is for a log.
 * - A plain `Error` — its `.message` is already the sentence.
 * - Anything else — a bare string, a Supabase Storage error shape, `undefined` from a
 *   rejected promise nobody typed. `String(error)` on those yields `"[object Object]"`,
 *   which tells the reader nothing; the caller's `fallback` is a sentence someone wrote.
 *
 * Detected structurally (a `rawMessage` string) rather than with `instanceof ConnectError`,
 * for the same reason `errorCodeOf` above is a property check: this package must not take a
 * dependency on the transport to render its own error text. It is also what makes the helper
 * correct for an error that crossed a serialisation boundary and came back as a lookalike.
 *
 * This is the frontend twin of the backend's `describeError()`
 * (`supabase/functions/_shared/utils/toError.ts`). `local/no-raw-error-message` in
 * `apps/client`/`packages/ui` fails any new `error instanceof Error ? error.message : …`,
 * which is the shape that spread this bug to fourteen call sites (FIN-719).
 */
/**
 * FIN-865/F8/AC10 — the short id (first 8 hex chars of the correlation id) a person can quote
 * to support. `apps/client`'s edgeFunctionProxy.ts lifts the edge function's error-body
 * `reference` field onto the thrown error two ways: a plain property (never `.details` —
 * FIN-789), read first below for a same-process caller; and `ConnectError`'s `.metadata` (a
 * real `Headers`, populated via the constructor's 3rd argument), which is what actually
 * survives a browser-side Connect call crossing the BFF's own wire serialization — a plain
 * property does not. Undefined for an error that never reached the server at all (a network
 * failure, a client-side throw) — there is nothing to look up in that case.
 */
export function referenceOf(error: unknown): string | undefined {
  if (error && typeof error === "object" && "reference" in error) {
    const reference = (error as { reference?: unknown }).reference
    if (typeof reference === "string" && reference.trim() !== "") return reference
  }
  const metadata = (error as { metadata?: Headers } | null | undefined)?.metadata
  const fromMetadata = metadata?.get?.(REFERENCE_HEADER)
  return fromMetadata && fromMetadata.trim() !== "" ? fromMetadata : undefined
}

/**
 * FIN-867: a duck-typed read of a Connect error's numeric `.code`, for the two states
 * FeedbackDialog branches on (rate limited, signed out) — same transport-agnostic property
 * check as `errorCodeOf`/`referenceOf` above, never an `instanceof ConnectError` import.
 */
export function connectCodeOf(error: unknown): number | undefined {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code
    return typeof code === "number" ? code : undefined
  }
  return undefined
}

/** Connect's `Code.ResourceExhausted` — `edgeFunctionProxy.ts` maps a 429 (EC5) to this. */
export const CONNECT_CODE_RESOURCE_EXHAUSTED = 8
/** Connect's `Code.Unauthenticated` — a 401 (EC7, an expired session). */
export const CONNECT_CODE_UNAUTHENTICATED = 16

/** Connect's `Code.Unknown`/`Internal`/`DataLoss` — every one a server fault, not a refusal. */
const SERVER_FAILURE_CONNECT_CODES = new Set([2, 13, 15])
/** `ServiceError.errorType`s whose message is written for a log (`RepositoryError`, `InternalError`). */
const SERVER_FAILURE_ERROR_CODES = new Set(["REPOSITORY_ERROR", "INTERNAL_ERROR"])

/** 5xx statuses whose message is curated copy written for a person (FIN-844). */
const CURATED_SERVER_STATUSES = new Set([501, 502, 503, 504])

/**
 * FIN-844/FIN-380 — whether a failed edge-function call's own message must be withheld from a
 * person: a `RepositoryError`/`InternalError` code, or a 5xx other than the curated 501–504. This
 * is the one rule both apps apply. `apps/client` reaches it through `userFacingErrorMessage`
 * below; the consultant app (`src/`), which reads the raw HTTP status and error body, calls it
 * directly.
 */
export function isServerFailureFault(fault: { status?: number; errorCode?: string }): boolean {
  if (fault.errorCode && SERVER_FAILURE_ERROR_CODES.has(fault.errorCode)) return true
  return fault.status !== undefined && fault.status >= 500 && !CURATED_SERVER_STATUSES.has(fault.status)
}

/**
 * FIN-844 — a 500's message names repository methods and schema detail, so it is never shown.
 * `edgeFunctionProxy.ts` maps only a 500 (and unmapped statuses) to `Code.Internal`; 501/502/503
 * carry curated copy and arrive as Unimplemented/Unavailable. `errorCode` is checked too, for a
 * same-process caller — it does not survive the Connect wire to the browser.
 */
function isServerFailure(error: unknown): boolean {
  if (isServerFailureFault({ errorCode: errorCodeOf(error) })) return true
  const code = connectCodeOf(error)
  return code !== undefined && SERVER_FAILURE_CONNECT_CODES.has(code)
}

/**
 * Looks a `ServiceError.errorType` up in the message catalog (`errors.codes.<errorType>`) and
 * returns the sentence in the user's language, `undefined` when that code has no entry, or `null`
 * when it has one that cannot be worded (the parameters it needs did not arrive).
 * `apps/client` builds one from `next-intl` (`lib/i18n/errors.ts`); this package only knows the shape.
 */
export type ErrorCodeTranslator = (code: string, params?: ErrorParams) => string | undefined | null

/**
 * `ServiceError.errorType`s are SCREAMING_SNAKE. Anything else is never looked up in the catalog: it
 * is a plain object, so a code like `constructor` would resolve to an inherited property and put the
 * key's own path on a screen.
 */
const ERROR_TYPE_SHAPE = /^[A-Z][A-Z0-9_]*$/

/** How the correlation reference reads after a message; the app supplies its language's wording. */
export type ReferenceFormatter = (reference: string) => string

/** The `errors` namespace of the message catalog, as either app's translator exposes it. */
export interface ErrorMessages {
  (key: string, values?: Record<string, string | number>): string
  has(key: string): boolean
}

export function userFacingErrorMessage(
  error: unknown,
  fallback: string,
  translateCode?: ErrorCodeTranslator,
  formatReference: ReferenceFormatter = (reference) => `Reference: ${reference}`,
): string {
  const reference = referenceOf(error)
  const withReference = (message: string): string =>
    [message, reference ? formatReference(reference) : ""].filter(Boolean).join(" ")

  if (isServerFailure(error)) return withReference(fallback)
  // A coded refusal with a catalog entry reads in the user's language; one without keeps the
  // backend's English sentence below (never blank, never a raw code).
  const code = errorCodeOf(error)
  const params = errorParamsOf(error)
  const localized = code && translateCode ? translateCode(code, params) : undefined
  if (localized) return withReference(localized)
  // A refusal with a catalog sentence it cannot fill, or one that sends parameters but has no entry
  // here, is a sentence with holes in it: the caller's own (localized) fallback reads better than
  // the backend's English or a half-filled catalog key.
  if (localized === null || (code && params && translateCode)) return withReference(fallback)
  if (error && typeof error === "object") {
    const raw = (error as { rawMessage?: unknown }).rawMessage
    if (typeof raw === "string" && raw.trim() !== "") return withReference(raw)
  }
  if (error instanceof Error && error.message.trim() !== "") return withReference(error.message)
  return withReference(fallback)
}

/**
 * `userFacingErrorMessage` bound to the `errors` catalog: a coded refusal with an entry
 * (`codes.<errorType>`) and the "Reference: …" suffix read in the user's language; anything else keeps
 * the backend's own sentence or the caller's fallback. Shared by the browser hook and the server helper.
 */
export function userFacingErrorIn(messages: ErrorMessages, error: unknown, fallback: string): string {
  return userFacingErrorMessage(
    error,
    fallback,
    (code, params) => {
      if (!ERROR_TYPE_SHAPE.test(code)) return undefined
      const key = `codes.${code}`
      if (!messages.has(key)) return undefined
      const text = messages(key, params)
      // next-intl answers a sentence it cannot format (a missing parameter) with the key's own path.
      return text.endsWith(key) ? null : text
    },
    (reference) => messages("reference", { reference }),
  )
}
